import React, { useEffect } from "react";
import ProgressiveImage from "./ProgressiveImage";
import styles from "./AnimatedHoopWithImage.module.css";

const HOOP_THICKNESS = 0;
const STAR_COUNT = 3;
const cx = 150;
const cy = 105;
const rx = 140;
const ry = 90;
const imageRx = 140;
const imageRy = 100;
const starOrbitRx = 130;
const starOrbitRy = 105;

function getStarPoints(cx: number, cy: number, spikes: number, outerRadius: number, innerRadius: number) {
  let rot = Math.PI / 2 * 3;
  let x = cx;
  let y = cy;
  let step = Math.PI / spikes;
  let points = "";

  for (let i = 0; i < spikes; i++) {
    x = cx + Math.cos(rot) * outerRadius;
    y = cy + Math.sin(rot) * outerRadius;
    points += `${x},${y} `;
    rot += step;

    x = cx + Math.cos(rot) * innerRadius;
    y = cy + Math.sin(rot) * innerRadius;
    points += `${x},${y} `;
    rot += step;
  }
  return points.trim();
}

const AnimatedHoopWithImage = React.memo(function AnimatedHoopWithImage({ lowSrc, highSrc, stroke1, stroke2 }: { lowSrc: string; highSrc: string; stroke1?: string; stroke2?: string }) {
  useEffect(() => {
    let frame: number;
    function animateStars() {
      const now = performance.now();
      // iterate groups, update polygon points and tip circles
      document.querySelectorAll<SVGGElement>(".hoop-star-group").forEach((group, i) => {
        const duration = 3000; // ms
        const delay = (i * duration) / STAR_COUNT;
        const t = ((now - delay) % duration) / duration;
        const angleRad = (360 * t) * Math.PI / 180;
        const x = cx + starOrbitRx * Math.cos(angleRad);
        const y = cy + starOrbitRy * Math.sin(angleRad);
      //  const points = getStarPoints(x, y, 5, 10, 7); // outer/inner radii tuned for rounded look
   const points = getStarPoints(x, y, 4, 10, 7);

        // update polygon
        const poly = group.querySelector("polygon.hoop-star-polygon");
        if (poly) poly.setAttribute("points", points);

        // parse points and update tip circles (outer points are at even indices)
        const parts = points.split(/\s+/).map(p => p.split(",").map(Number));
        const tipCircles = group.querySelectorAll<SVGCircleElement>("circle.hoop-star-tip");
        // update each tip circle position (only outer points)
        let tipIndex = 0;
        for (let pi = 0; pi < parts.length; pi += 2) {
          const pt = parts[pi];
          const cxv = String(pt[0]);
          const cyv = String(pt[1]);
          const c = tipCircles[tipIndex++];
          if (c) {
            c.setAttribute("cx", cxv);
            c.setAttribute("cy", cyv);
          }
        }
      });
      frame = requestAnimationFrame(animateStars);
    }
    animateStars();
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div className={styles.animatedHoopContainer}>
      <div className={styles.animatedHoopInner}>
        <img src="highSrc" alt=""/>
        {/* <ProgressiveImage
          lowSrc={lowSrc}
          highSrc={highSrc}
          // className = "responsive-img"
          // className="magnification"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 1,
            position: "relative",
          }}
        /> */}
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 300 200"
          className={styles.animatedHoopSvg}
        >
          <defs>
            <filter id="star-glow-shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="0.1" result="glow" />
              {/* <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor={"#00ffdd"} floodOpacity={"0.6"} result="shadow" /> */}
              <feMerge>
                <feMergeNode in="glow" />
                <feMergeNode in="shadow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            <linearGradient id="star-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              {/* <stop offset="0%" stopColor="#00ffdd" />
              <stop offset="100%" stopColor="#00ff9d" /> */}
              <stop offset="0%" stopColor="#FFD700" />
              <stop offset="100%" stopColor="#FFD700" />
            </linearGradient>
            <radialGradient id="star-3d-gradient" cx="50%" cy="50%" r="50%">
              <stop offset="30%" stopColor="#fffb00ff" stopOpacity="0.8" />
              <stop offset="60%" stopColor="#d0ff00ff" />
              <stop offset="100%" stopColor="#FFD700" />
            </radialGradient>
          </defs>

          <image
            href={highSrc}
            x={cx - imageRx}
            y={cy - imageRy}
            width={imageRx * 2}
            height={imageRy * 2}
            className="magnification"
            clipPath="url(#ellipse-clip)"
            preserveAspectRatio="xMidYMid slice"
          />

          <ellipse
            cx={cx}
            cy={cy}
            rx={imageRx}
            ry={imageRy}
            stroke={stroke1}
            strokeWidth={HOOP_THICKNESS}
            fill="none"
          />

          {Array.from({ length: STAR_COUNT }).map((_, i) => {
            // initial placement (will be updated by animation)
            const pts = getStarPoints(cx, cy - starOrbitRy, 4, 10, 7);
            const parts = pts.split(/\s+/).map(p => p.split(",").map(Number));
            const outerPoints = parts.filter((_, idx) => idx % 2 === 0);
            return (
              <g key={`star-group-${i}`} className="hoop-star-group" opacity="0.9" filter="url(#star-glow-shadow)">
                <polygon
                  className="hoop-star-polygon"
                  points={pts}
                  // fill="url(#star-3d-gradient)"
                  fill="url(#star-gradient)"
                  // fill="#00ffdd"
                  stroke="none"
                  vectorEffect="non-scaling-stroke"
                />
                {/* small circles at outer tips to visually round tips */}
                {outerPoints.map((pt, j) => (
                  <circle
                    key={`tip-${i}-${j}`}
                    className="hoop-star-tip"
                    cx={pt[0]}
                    cy={pt[1]}
                    r={2.6}                // slightly larger to cover anti-alias seams
                    // fill="url(#star-3d-gradient)"
                    //fill="#00ffdd"
                    fill="url(#star-gradient)"
                    fillOpacity={1}
                    stroke="none"
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
});

export default AnimatedHoopWithImage;